import React, { useState, useRef, useCallback, useEffect } from 'react'
import { Button, Card, CardBody, Input, Label, Row, Col, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import "../../assets/css/--component-authentication.css"
import chatAi from "../../assets/images/chatAI.png";
import waves from "../../assets/images/waves.png";
import { extractDynamicVariables } from '../../helper/dynamicVariables';
import useAgentTestSession from '../../hooks/useAgentTestSession';

const TestAgentComponent = ({ agent = {} }) => {
    const agentId = agent?.agentId;
    const chatRef = useRef(null);

    const {
        messages,
        callSummary,
        inCall,
        connecting,
        callTimer,
        micError,
        timeLimitReached,
        maxDurationSeconds,
        startTest,
        endTest: endTestHandler,
        startNewTest,
    } = useAgentTestSession(agentId);

    const [dynamicVariableModal, setDynamicVariableModal] = useState(false);
    const [dynamicFields, setDynamicFields] = useState([]);
    const [dynamicVariableValues, setDynamicVariableValues] = useState({});
    const [dynamicVariableErrors, setDynamicVariableErrors] = useState({});

    useEffect(() => {
        chatRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const openDynamicVariableModal = (fields) => {
        const nextValues = {};

        fields.forEach((field) => {
            nextValues[field] = dynamicVariableValues[field] || "";
        });

        setDynamicFields(fields);
        setDynamicVariableValues(nextValues);
        setDynamicVariableErrors({});
        setDynamicVariableModal(true);
    };

    const handleDynamicVariableChange = (field, value) => {
        setDynamicVariableValues((prev) => ({
            ...prev,
            [field]: value,
        }));

        setDynamicVariableErrors((prev) => ({
            ...prev,
            [field]: "",
        }));
    };

    const validateDynamicVariables = () => {
        const nextErrors = {};
        let hasError = false;

        dynamicFields.forEach((field) => {
            if (!dynamicVariableValues[field] || !dynamicVariableValues[field].trim()) {
                nextErrors[field] = "This field is required";
                hasError = true;
            }
        });

        setDynamicVariableErrors(nextErrors);
        return !hasError;
    };

    const handleDynamicVariableSubmit = () => {
        if (!validateDynamicVariables()) {
            return;
        }

        setDynamicVariableModal(false);
        startTest(dynamicVariableValues);
    };

    const handleStartTestClick = useCallback(() => {
        const fields = extractDynamicVariables(`${agent?.prompt || ""} ${agent?.firstMessage || ""}`);

        if (fields.length > 0) {
            openDynamicVariableModal(fields);
            return;
        }

        startTest({});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agent, startTest, dynamicVariableValues]);

    return (
        <div className="container-fluid" style={{ paddingBottom: "40px" }}>
            {!inCall && !connecting && !messages.length && !callSummary ? (
                <div className="d-flex flex-column align-items-center justify-content-center text-center py-5">
                    <h4 className="mb-2">Test Agent</h4>
                    <p className="text-muted fs-12 mb-4">
                        Start a voice test session using your microphone — no phone call is made and no call log is created.
                    </p>
                    {micError && <div className="invalid-feedback d-block mb-3">{micError}</div>}
                    <Button onClick={handleStartTestClick} className="px-4 py-2 rounded linear-gradient-btn">Start Test</Button>
                </div>
            ) : (
                <>
                    {connecting && (
                        <div className="d-flex flex-column align-items-center justify-content-center text-center py-5">
                            <span className="spinner-border spinner-border-sm mb-3" role="status" aria-hidden="true"></span>
                            <p className="text-muted">Connecting to agent...</p>
                        </div>
                    )}

                    {inCall && !callSummary && (
                        <div className="call-container">
                            <div className='d-flex flex-column align-items-center'>
                                <div className="call-timer">
                                    <div className="wave-container">
                                        <img src={waves} alt="Waves" className="wave-image" />
                                    </div>
                                    <div className="timer-text">{callTimer}</div>
                                </div>
                                <span onClick={endTestHandler} className='text-center bg-danger cursor-pointer d-block rounded-5 py-2 text-white d-flex justify-content-center align-items-center gap-1 w-40 fw-500'>End Call</span>
                            </div>
                            <Card className="chat-box">
                                <CardBody className="chat-body">
                                    <h3 className="chat-header">Test in progress...</h3>
                                    <div className="chat-messages">
                                        {messages.map((msg, index) => (
                                            <div key={index} className={`message-row ${msg.sender === "user" ? "user-message" : "bot-message"}`}>
                                                {msg.sender === "bot" && (
                                                    <div className="bot-icon">
                                                        <img src={chatAi} alt="AI" className="chat-ai-image" />
                                                    </div>
                                                )}
                                                <div className={`message-content ${msg.sender === "user" ? "user-bubble" : "bot-bubble"}`}>
                                                    {msg.text}
                                                </div>
                                                {msg.sender === "user" && <div className="user-icon">YOU</div>}
                                            </div>
                                        ))}
                                        <div ref={chatRef}></div>
                                    </div>
                                </CardBody>
                            </Card>
                        </div>
                    )}

                    {callSummary && (
                        <div className="call-summary-container">
                            <div className="timer-container">
                                <div className="call-ended">
                                    <div className="wave-container">
                                        <img src={waves} alt="Waves" className="wave-image" />
                                    </div>
                                    <h3 className="call-ended-text">Test Ended</h3>
                                </div>
                            </div>
                            <div className="summary-container">
                                <Card className="summary-box">
                                    <CardBody className="summary-body">
                                        <h3 className="summary-header">Here's the Summary of your test</h3>
                                        {timeLimitReached && (
                                            <p className="text-muted fs-12 mb-2">This test reached the {Math.round((maxDurationSeconds || 180) / 60)}-minute limit for test calls.</p>
                                        )}
                                        <div className="summary-text">{callSummary}</div>
                                    </CardBody>
                                </Card>
                                <Button onClick={startNewTest} className="d-inline-block font-normal text-center py-2 px-4 rounded fs-16 text-decoration-none text-white linear-gradient-btn w-100 border-0">Test Again</Button>
                            </div>
                        </div>
                    )}
                </>
            )}

            <Modal
                isOpen={dynamicVariableModal}
                toggle={() => setDynamicVariableModal(false)}
                centered={true}
                size="md"
                keyboard={false}
            >
                <ModalHeader toggle={() => setDynamicVariableModal(false)}>
                    Fill Dynamic Variables
                </ModalHeader>

                <ModalBody>
                    <Row>
                        {dynamicFields.map((field) => (
                            <Col md={12} className="mb-3" key={field}>
                                <Label className="text-left text-black font-bold">
                                    {field}
                                </Label>
                                <Input
                                    type="text"
                                    className="custom-input rounded border-0 py-2"
                                    value={dynamicVariableValues[field] || ""}
                                    onChange={(e) => handleDynamicVariableChange(field, e.target.value)}
                                    placeholder={`Enter ${field}`}
                                />
                                {dynamicVariableErrors[field] && (
                                    <p className="invalid-feedback">{dynamicVariableErrors[field]}</p>
                                )}
                            </Col>
                        ))}
                    </Row>
                </ModalBody>

                <ModalFooter>
                    <Button
                        type="button"
                        className="px-4 py-2 rounded btn-danger"
                        onClick={() => setDynamicVariableModal(false)}
                    >
                        Cancel
                    </Button>

                    <Button
                        type="button"
                        className="px-4 py-2 rounded linear-gradient-btn"
                        onClick={handleDynamicVariableSubmit}
                    >
                        Continue
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default TestAgentComponent;
